import CloseIcon from '@mui/icons-material/AddRounded'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNewRounded'

import { addKeyDownListener, removeKeyDownListener } from '@utils/listeners'

import { Portal } from '@components/Portal'

import {
  BottomContainer,
  CancelButton,
  CloseButton,
  Container,
  HeaderContainer,
  HeaderTitleContainer,
  MainContainer,
  OverlayContainer,
  PopUpContainer,
  PreviousButton,
  ScrollContainer,
  SubmitButton,
  Title,
  TitleContainer,
} from './PopUp.styled'
import { Button } from '@components/Button'

interface PopUpProps extends PopUpSkeletonProps {}

export const PopUp = (props: PopUpProps): JSX.Element => {
  const { autoHideOnSubmit, children, onSubmit, onClose, ...skeletonProps } = props

  const refComponent = useRef<string>(v4())

  useEffect(() => {
    const dismiss = (e: KeyboardEvent) => {
      if (e.key == 'Escape') {
        onClose()
      }
    }

    addKeyDownListener({ key: refComponent.current, method: dismiss })

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      removeKeyDownListener({ key: refComponent.current, method: dismiss })
    }
  }, [onClose])

  const [display, setDisplay] = useState<boolean>(false)

  useEffect(() => {
    setTimeout(() => {
      setDisplay(true)
    }, 10)
  }, [])

  return (
    <Portal
      style={{
        top: 0,
        left: 0,
        zIndex: 1000,
      }}
    >
      <OverlayContainer open={display} />

      <Container id="popup" open={display}>
        <PopUpSkeleton
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()

            if (autoHideOnSubmit) setDisplay(false)

            setTimeout(() => {
              onSubmit(e)
            }, 150)
          }}
          onClose={() => {
            setDisplay(false)

            setTimeout(() => {
              onClose()
            }, 150)
          }}
          {...skeletonProps}
        >
          {children}
        </PopUpSkeleton>
      </Container>
    </Portal>
  )
}

interface PopUpSkeletonProps {
  children?: React.ReactNode
  fullScreen?: boolean
  type?: 'validate' | 'destroy' | 'primary'

  title: string

  onClose: () => void
  onCloseLabel?: string

  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  onSubmitLabel: string

  onSubmitDisabled?: boolean

  showPrevious?: boolean
  onPrevious?: () => void

  showFooter?: boolean
  autoHideOnSubmit?: boolean

  overflow?: 'auto' | 'hidden'

  HeaderChildren?: () => JSX.Element

  style?: React.CSSProperties
}

export const PopUpSkeleton = (props: PopUpSkeletonProps): JSX.Element => {
  const {
    children,
    title,
    onClose,
    onCloseLabel,
    onSubmit,
    onSubmitLabel,
    showFooter = true,
    onPrevious,
    showPrevious,
    fullScreen,
    type = 'validate',
    onSubmitDisabled,
    HeaderChildren,
    overflow = 'auto',
    style,
  } = props

  const { t } = useTranslation()

  return (
    <PopUpContainer onSubmit={onSubmit} style={style}>
      <HeaderContainer hasChildren={!!HeaderChildren}>
        <HeaderTitleContainer>
          <TitleContainer>
            {showPrevious && onPrevious && (
              <PreviousButton onClick={onPrevious}>
                <ArrowBackIosNewIcon />
              </PreviousButton>
            )}
            <Title>{title}</Title>
          </TitleContainer>

          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </HeaderTitleContainer>

        {HeaderChildren && <HeaderChildren />}
      </HeaderContainer>

      <ScrollContainer overflow={overflow}>
        <MainContainer fullScreen={fullScreen}>{children}</MainContainer>
      </ScrollContainer>

      {showFooter && (
        <BottomContainer>
          <CancelButton type="button" onClick={onClose}>
            {onCloseLabel || t('components_PopUp_label_close')}
          </CancelButton>

          {(type == 'validate' || type == 'destroy') && (
            <SubmitButton type="submit" destroy={type == 'destroy'} disabled={onSubmitDisabled}>
              {onSubmitLabel}
            </SubmitButton>
          )}

          {type == 'primary' && (
            <Button variant="solid" color="primary" size={{ default: 'medium' }}>
              {onSubmitLabel}
            </Button>
          )}
        </BottomContainer>
      )}
    </PopUpContainer>
  )
}
